/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA;
 */

// keys for the tabs on Monitoring view
export const tabKeys = Object.freeze({
    notStarted: 'notStarted',
    awaitingAuthorization: 'awaitingAuthorization',
    inProgress: 'inProgress',
    paused: 'paused',
    finished: 'finished'
});
export const tabKeysEnableToHide = Object.freeze({
    notStarted: 'not-started',
    awaitingAuthorization: 'awaiting-authorization',
    paused: 'paused'
});

// Status values, property of received interactions - governs the tabs contents
export const deliveryExecutionStatuses = Object.freeze({
    initial: 'initial', // => notStarted tab
    authorized: 'authorized', // => notStarted tab
    interacting: 'interacting', // => inProgress tab
    suspended: 'suspended', // => paused tab
    terminated: 'terminated', // => finished tab
    closed: 'closed', // => finished tab
    canceled: 'canceled', // => not yet displayed in any tab
    deleted: 'deleted' // => inProgress tab
});

// Supported ACS actions. These names are proctoring business level, LTI-BE maps them to LTI standard
export const actionsList = Object.freeze({
    authorize: 'authorize',
    report: 'report', // 'flag' on LTI standard
    terminate: 'terminate',
    pause: 'pause',
    resume: 'resume',
    reopen: 'reopen', // 'resume' on LTI standard
    extendTime: 'extend_time',
    resetExtraTime: 'reset_extra_time',
    reset: 'reset'
});

// Mapping of ACS actions to LTI standard actions
export const actionsACSActionsMap = Object.freeze({
    extendTime: 'update',
    resetExtraTime: 'update'
});

// Icons attached to each action
export const actionsIcons = {
    [actionsList.authorize]: 'play',
    [actionsList.report]: 'flag-outline',
    [actionsList.terminate]: 'terminate',
    [actionsList.reopen]: 'redo',
    [actionsList.pause]: 'pause',
    [actionsList.resume]: 'play',
    [actionsList.extendTime]: 'clock',
    [actionsList.resetExtraTime]: 'trashcan',
    [actionsList.reset]: 'refresh'
};

// Available proctoring routes
export const routes = Object.freeze({
    home: '/',
    error: '/error',
    details: '/details',
    authorize: '/authorize',
    report: '/report',
    reset: '/reset',
    terminate: '/terminate',
    reopen: '/reopen',
    pause: '/pause',
    resume: '/resume',
    retryAuthorize: '/retry-authorize',
    extendTime: '/extend-time',
    resetExtraTime: '/reset-extra-time',
    retryReport: '/retry-report',
    retryReset: '/retry-reset',
    retryTerminate: '/retry-terminate',
    retryReopen: '/retry-reopen',
    retryPause: '/retry-pause',
    retryResume: '/retry-resume',
    retryExtendTime: '/retry-extend-time'
});

// Available proctoring endpoints
export const endpoints = Object.freeze({
    init: '/init',
    refreshToken: '/api/v1/auth/refresh-tokens',
    tenantConfiguration: '/api/v1/configuration',
    authorize: '/api/v1/assessments/authorize',
    acs: '/api/v1/actions/acs',
    acsEvents: '/api/v1/acs/:id/log',
    sessions: '/sessions',
    deliveryExecution: '/api/v1/delivery-executions/:id'
});

// lti claims checked on proctoring FE
export const ltiClaims = Object.freeze({
    launch_presentation: 'https://purl.imsglobal.org/spec/lti/claim/launch_presentation',
    custom: 'https://purl.imsglobal.org/spec/lti/claim/custom',
    context: 'https://purl.imsglobal.org/spec/lti/claim/context',
    nrps: 'https://purl.imsglobal.org/spec/lti-nrps/claim/namesroleservice'
});

export const recoverableErrors = Object.freeze({
    internetConnection: 'internetConnection',
    websocket: 'ws',
    nrps: 'nrps',
    interactions: 'interactions',
    multiCause: 'multiCause'
});

export const notificationPersistenceOptions = {
    autoclose: 'autoclose',
    pagePersistent: 'pagePersistent',
    persistent: 'persistent'
};

/**
 * Keys of interaction timer data, describing to which scope is timer applied:
 * `interaction.timers = {test: .., item: ..., extra: ...}`
 */
export const timerLevels = {
    test: 'test',
    part: 'part',
    section: 'section',
    item: 'item',
    extra: 'extra'
};

export const ROLE_ADMIN = 'ADMIN';
export const ROLE_TEST_TAKER = 'TEST_TAKER';
export const ROLE_BATTERY = 'BATTERY';
export const ROLE_CONTENT_CREATOR = 'CONTENT_CREATOR';
export const ROLE_DASHBOARD_VIEWER = 'DASHBOARD_VIEWER';
export const ROLE_DATA_EXPLORER = 'DATA_EXPLORER';
export const ROLE_GRADER = 'GRADER';
export const ROLE_GROUP_CREATOR = 'GROUP_CREATOR';
export const ROLE_GROUP_MANAGER = 'GROUP_MANAGER';
export const ROLE_OFFLINE_UPLOADER = 'OFFLINE_UPLOADER';
export const ROLE_ONLINE_UPLOADER = 'ONLINE_UPLOADER';

export const ROLE_REVIEW_GRADER = 'REVIEW_GRADER';

export const ROLE_BACKOFFICE = 'ROLE_BACKOFFICE';
